import { demoFunc } from './dees-harness-status.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessStatus } from '../interfaces.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-status': DeesHarnessStatus;
  }
}

/** matches --dees-transition-default (0.2s) — the CSS leave transition and the
 * DOM removal below must stay paired */
const leaveDurationMs = 200;

/**
 * Live status line for an agent run: idle (renders nothing unless
 * showIdle), busy with animated dots, or error. Enter and leave are
 * animated — on a transition to idle the previous status keeps rendering
 * with a leave transition before the element empties, so consumers can
 * bind `.status` directly without orchestrating exit animations.
 * aria-live so screen readers announce transitions.
 */
@customElement('dees-harness-status')
export class DeesHarnessStatus extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor status: IHarnessStatus = { type: 'idle' };

  @property({ type: Boolean })
  accessor showIdle: boolean = false;

  @state()
  accessor leaving: boolean = false;

  private lastVisibleStatus: IHarnessStatus | null = null;
  private leaveTimeout: ReturnType<typeof setTimeout> | null = null;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .status {
        display: flex;
        align-items: center;
        margin: var(--dees-spacing-xs) 0;
        gap: var(--dees-spacing-sm);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
        line-height: 1.4;
        white-space: pre-wrap;
        animation: harness-status-enter var(--dees-transition-default) var(--dees-ease-out);
        transition:
          opacity var(--dees-transition-default) var(--dees-ease-in),
          transform var(--dees-transition-default) var(--dees-ease-in);
      }

      .status.leaving {
        opacity: 0;
        transform: translateY(2px);
      }

      @keyframes harness-status-enter {
        from {
          opacity: 0;
          transform: translateY(-2px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .status.error {
        color: var(--dees-color-text-error);
      }

      .dots {
        display: inline-flex;
        gap: 3px;
      }

      .dots span {
        width: 5px;
        height: 5px;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-accent-primary);
        animation: harness-status-pulse 1.2s ease-in-out infinite;
      }

      .dots span:nth-child(2) {
        animation-delay: 0.15s;
      }

      .dots span:nth-child(3) {
        animation-delay: 0.3s;
      }

      @keyframes harness-status-pulse {
        0%, 80%, 100% {
          opacity: 0.25;
          transform: scale(0.8);
        }
        40% {
          opacity: 1;
          transform: scale(1);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .dots span { animation: none; }
        .status {
          animation: none;
          transition: none;
        }
      }
    `,
  ];

  public willUpdate(changedProperties: Map<string, any>): void {
    super.willUpdate(changedProperties);
    if (!changedProperties.has('status')) return;
    if (this.status.type !== 'idle') {
      this.lastVisibleStatus = this.status;
      this.leaving = false;
      this.clearLeaveTimeout();
    } else if (this.lastVisibleStatus && !this.showIdle) {
      // keep the previous status rendered while it fades out
      this.leaving = true;
      this.clearLeaveTimeout();
      this.leaveTimeout = setTimeout(() => {
        this.leaveTimeout = null;
        this.lastVisibleStatus = null;
        this.leaving = false;
      }, leaveDurationMs);
    }
  }

  public render(): TemplateResult {
    if (this.status.type === 'idle' && !this.showIdle && !this.leaving) {
      return html``;
    }
    const shown = this.leaving ? this.lastVisibleStatus! : this.status;
    return html`
      <div class="status ${shown.type} ${this.leaving ? 'leaving' : ''}" aria-live="polite" role="status">
        ${shown.type === 'busy'
          ? html`<span class="dots"><span></span><span></span><span></span></span>`
          : ''}
        <span>${this.statusText(shown)}</span>
      </div>
    `;
  }

  private statusText(status: IHarnessStatus): string {
    if (status.message) return status.message;
    if (status.type === 'busy') return 'Working…';
    if (status.type === 'error') return 'Something went wrong.';
    return 'Idle';
  }

  private clearLeaveTimeout(): void {
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
      this.leaveTimeout = null;
    }
  }

  public async disconnectedCallback(): Promise<void> {
    this.clearLeaveTimeout();
    await super.disconnectedCallback();
  }
}
